import { Box, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Sliders } from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import UpgradeButton from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/ShiftActionsBar/UpgradeButton'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { trackViewOptionsEvent } from 'modules/shift_management/lib/trackEvents'

import Content from './Content'

interface Props {
  disabled: boolean | undefined
  canManageShifts: boolean
}

const ViewOptionsSectionButton = observer(({ disabled, canManageShifts }: Props) => {
  const [{ view }] = useSchedule()
  const [{ canUseCoverageView, canUseViewOptions }] = useEnabledFeatures()
  const { viewOptions } = useCurrentViewOptions()
  const [isSectionOpen, setIsSectionOpen] = useToggle(false)
  const [isTooltipOpen, setIsTooltipOpen] = useToggle(false)
  const open = isTooltipOpen && !isSectionOpen
  const hasCoverage = canUseCoverageView && view.isWeekly && view.isByEmployees
  const label = i18n.t('shift_management.view_options.mainTitle')

  const onClick = (isOpen: boolean) => {
    trackEvent(isOpen)
    setIsSectionOpen(!isSectionOpen)()
  }

  const trackEvent = (isOpen: boolean) => {
    trackViewOptionsEvent(isOpen, view, viewOptions, hasCoverage)
  }

  // We don't want to show the button if company does not have feature
  // and user is a basic employee (for managers we will show the upgrade button)
  // or if the table is empty
  const doNotShowButton = disabled || (!canUseViewOptions && !canManageShifts)

  if (doNotShowButton) {
    return null
  }

  if (!canUseViewOptions)
    return (
      <Box
        flexDirection="row"
        maxWidth="s120"
        minHeight="s40"
        overflowX="hidden"
        position="relative"
        alignItems="center"
      >
        <Tooltip.Alt attachment="top center" label={label} open={open} toggle={setIsTooltipOpen}>
          <UpgradeButton feature={Features.shift_management_view_options} disabled={disabled} />
        </Tooltip.Alt>
      </Box>
    )

  return (
    <Box
      flexDirection="row"
      maxWidth="s120"
      minHeight="s40"
      overflowX="hidden"
      position="relative"
      alignItems="center"
    >
      <PopoverMenu type="list" onClick={onClick}>
        <PopoverMenu.Trigger disabled={disabled}>
          <Tooltip.Alt attachment="top center" label={label} open={open} toggle={setIsTooltipOpen}>
            <F0Button
              variant="outline"
              icon={Sliders}
              size="md"
              hideLabel
              label={i18n.t('shift_management.actions.view_options')}
            />
          </Tooltip.Alt>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content
          side="bottom"
          align="start"
          width="s300"
          paddingTop="s0"
          paddingBottom="s0"
        >
          <Content viewSelectorDisabled={!!disabled} />
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
})

export { ViewOptionsSectionButton }
